import { useState, useEffect, useCallback } from "react";
import type { Doctor } from "@/types";
import { dbGet, dbSet } from "@/lib/db";
import { SK } from "@/lib/constants";

export function useDoctors() {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dbGet<Doctor[]>(SK.DOCTORS).then((d) => {
      setDoctors(d ?? []);
      setLoading(false);
    });
  }, []);

  const persist = useCallback((next: Doctor[]) => {
    setDoctors(next);
    dbSet(SK.DOCTORS, next);
  }, []);

  const addDoctor = useCallback(
    (doc: Omit<Doctor, "id">) => {
      const newDoc: Doctor = { ...doc, id: "D" + Date.now() };
      persist([...doctors, newDoc]);
      return newDoc;
    },
    [doctors, persist]
  );

  const updateDoctor = useCallback(
    (id: string, updates: Partial<Doctor>) => {
      persist(doctors.map((d) => (d.id === id ? { ...d, ...updates } : d)));
    },
    [doctors, persist]
  );

  const deleteDoctor = useCallback(
    (id: string) => {
      persist(doctors.filter((d) => d.id !== id));
    },
    [doctors, persist]
  );

  const setAllDoctors = useCallback(
    (docs: Doctor[]) => persist(docs),
    [persist]
  );

  return { doctors, loading, addDoctor, updateDoctor, deleteDoctor, setAllDoctors };
}
